import { useEffect } from 'react'
import { useAuth } from '../auth/useAuth'
import { audioSystem } from '../pc/feel/audio'
import { useArenaStore } from '../store'
import { INPUT_PRIORITY, modalInputRouter } from '../input/modalInputRouter'

export function PauseMenu() {
  const isPaused           = useArenaStore((s) => s.isPaused)
  const setIsPaused        = useArenaStore((s) => s.setIsPaused)
  const mouseSensitivity   = useArenaStore((s) => s.mouseSensitivity)
  const setMouseSensitivity = useArenaStore((s) => s.setMouseSensitivity)
  const audioMuted         = useArenaStore((s) => s.audioMuted)
  const setAudioMuted      = useArenaStore((s) => s.setAudioMuted)
  const masterVolume       = useArenaStore((s) => s.masterVolume)
  const setMasterVolume    = useArenaStore((s) => s.setMasterVolume)
  const viewBobEnabled     = useArenaStore((s) => s.viewBobEnabled)
  const setViewBobEnabled  = useArenaStore((s) => s.setViewBobEnabled)
  const { logout } = useAuth()

  // Esc key resumes
  useEffect(() => {
    if (!isPaused) return
    return modalInputRouter.register(INPUT_PRIORITY.pause, (e) => {
      if (e.key === 'Escape') {
        resume()
        return true
      }
      return e.code === 'KeyE' || e.code === 'KeyM' || e.key === 'Tab'
    })
  }) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isPaused) return null

  function resume() {
    audioSystem.playUIClick()
    setIsPaused(false)
    document.querySelector('canvas')?.requestPointerLock()
  }

  function handleLogout() {
    audioSystem.playUIClick()
    setIsPaused(false)
    logout()
  }

  function toggleMute() {
    audioSystem.playUIClick()
    setAudioMuted(!audioMuted)
  }

  function toggleBob() {
    audioSystem.playUIClick()
    setViewBobEnabled(!viewBobEnabled)
  }

  return (
    <div style={s.overlay} data-testid="pause-menu">
      <div style={s.card}>
        <h2 style={s.title}>Paused</h2>

        <button style={s.resumeBtn} onClick={resume} autoFocus>
          ▶ &nbsp;Resume
        </button>

        <div style={s.divider} />

        <div style={s.sensitivityRow}>
          <label style={s.sensitivityLabel} htmlFor="sensitivity-slider">
            Mouse Sensitivity
          </label>
          <div style={s.sliderRow}>
            <span style={s.sliderVal}>{mouseSensitivity.toFixed(1)}×</span>
            <input
              id="sensitivity-slider"
              type="range"
              min={0.1}
              max={3}
              step={0.1}
              value={mouseSensitivity}
              onChange={(e) => setMouseSensitivity(Number(e.target.value))}
              style={s.slider}
            />
          </div>
        </div>

        <div style={s.divider} />

        <div style={s.sensitivityRow}>
          <label style={s.sensitivityLabel} htmlFor="volume-slider">
            Master Volume
          </label>
          <div style={s.sliderRow}>
            <span style={s.sliderVal}>{Math.round(masterVolume * 100)}%</span>
            <input
              id="volume-slider"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={masterVolume}
              onChange={(e) => setMasterVolume(Number(e.target.value))}
              style={s.slider}
              data-testid="volume-slider"
            />
          </div>
        </div>

        <div style={s.divider} />

        <div style={s.toggleRow}>
          <span style={s.toggleLabel}>Sound</span>
          <button
            style={{ ...s.toggleBtn, ...(audioMuted ? s.toggleOff : s.toggleOn) }}
            onClick={toggleMute}
            data-testid="mute-toggle"
          >
            {audioMuted ? '🔇 Muted' : '🔊 On'}
          </button>
        </div>

        <div style={s.toggleRow}>
          <span style={s.toggleLabel}>View Bob</span>
          <button
            style={{ ...s.toggleBtn, ...(viewBobEnabled ? s.toggleOn : s.toggleOff) }}
            onClick={toggleBob}
            data-testid="viewbob-toggle"
          >
            {viewBobEnabled ? 'On' : 'Off'}
          </button>
        </div>

        <div style={s.divider} />

        <button style={s.signOutBtn} onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(6px)',
    zIndex: 30,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '2rem 2.5rem',
    background: 'rgba(61,64,91,0.92)',
    border: '1px solid rgba(245,235,224,0.15)',
    borderRadius: '1.25rem',
    boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
    backdropFilter: 'blur(16px)',
    minWidth: '20rem',
    maxWidth: '28rem',
    width: '88vw',
  },
  title: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#f5ebe0',
    letterSpacing: '0.04em',
    textAlign: 'center',
  },
  resumeBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '1rem',
    fontWeight: 700,
    padding: '0.7rem 1.5rem',
    borderRadius: '9999px',
    border: 'none',
    background: 'linear-gradient(135deg, #e07a5f, #b08968)',
    color: '#f5ebe0',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(224,122,95,0.35)',
    alignSelf: 'center',
  },
  divider: {
    height: '1px',
    background: 'rgba(245,235,224,0.08)',
    margin: '0 -0.5rem',
  },
  sensitivityRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  sensitivityLabel: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'rgba(245,235,224,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  sliderVal: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#f5ebe0',
    minWidth: '2.5rem',
    textAlign: 'right',
  },
  slider: {
    flex: 1,
    accentColor: '#e07a5f',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  toggleLabel: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'rgba(245,235,224,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  toggleBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.78rem',
    fontWeight: 700,
    padding: '0.3rem 0.75rem',
    borderRadius: '9999px',
    border: '1px solid',
    cursor: 'pointer',
    minWidth: '4.5rem',
    textAlign: 'center',
  },
  toggleOn: {
    background: 'rgba(163,177,138,0.15)',
    borderColor: 'rgba(163,177,138,0.4)',
    color: '#a3b18a',
  },
  toggleOff: {
    background: 'rgba(245,235,224,0.06)',
    borderColor: 'rgba(245,235,224,0.12)',
    color: 'rgba(245,235,224,0.35)',
  },
  signOutBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.85rem',
    fontWeight: 700,
    padding: '0.5rem 1rem',
    borderRadius: '0.625rem',
    border: '1px solid rgba(248,113,113,0.3)',
    background: 'rgba(248,113,113,0.08)',
    color: '#f87171',
    cursor: 'pointer',
    alignSelf: 'center',
  },
}
